import React, { useState } from 'react'
import { useParams } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { useTeams } from '../../contexts/TeamsContext';
import { Status111Enum, StealerLeak, StealerLeakSeverityEnum, TwoFaStatusEnum, ValidityEnum } from 'api-client/models';
import { CredentialsApi } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Key, Download, RefreshCw, AlertTriangle, Shield, CheckCircle, Check } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import AssetNotes from '@/components/assets/detail/AssetNotes';
import { FilterSelect } from '@/components/findings/filters/FilterSelect';
import { toast } from 'sonner';

const formatTimestamp = (timestamp: Date | string | undefined): string => {
  if (!timestamp) return '';
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

function StealerLeakDetail() {
  const { id } = useParams<{ id: string }>();
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leak, setLeak] = useState<StealerLeak | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  
  const [impactText, setImpactText] = useState<string>('');
  const [recommendationText, setRecommendationText] = useState<string>('');

  const fetchLeak = useCallback(async () => {
    if (!currentTeam?.slug || !token || !id) return;

    setLoading(true);
    setError(null);

    try {
      const api = new CredentialsApi(getApiConfiguration(token));
      const response = await api.stealerLeaksRetrieve({
        id: id,
        teamSlug: currentTeam.slug,
      });
      console.log(response)
      setLeak(response);
    } catch (err) {
      console.error('Error fetching stealer leak:', err);
      setError('Failed to load stealer leak');
    } finally {
      setLoading(false);
    }
  }, [currentTeam?.slug, token, id]);

  useEffect(() => {
    fetchLeak();
  }, [fetchLeak]);

  // Update local state when leak data is loaded
  useEffect(() => {
    if (leak) {
      setImpactText(leak.impact || '');
      setRecommendationText(leak.recommendation || '');
    }
  }, [leak]);

  const handleAttributeChange = async (attribute: string, value: any) => {
    if (!currentTeam?.slug || !token || !id || !leak) return;
    
    // Check if user is admin
    if (!currentTeam.isAdmin) {
      toast.error('Only administrators can modify stealer leak attributes');
      return;
    }
    
    setIsUpdating(true);
    try {
      const api = new CredentialsApi(getApiConfiguration(token));
      await api.credentialsStealerLeaksPartialUpdate({
        id,
        teamSlug: currentTeam.slug,
        patchedStealerLeak: {
          [attribute]: value
        }
      });
      
      // Update local state
      setLeak(prev => prev ? { ...prev, [attribute]: value } : null);
      toast.success(`Updated ${attribute} successfully`);
    } catch (err) {
      console.error(`Error updating ${attribute}:`, err);
      toast.error(`Failed to update ${attribute}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // Get severity color based on severity level
  const getSeverityColor = (severity?: StealerLeakSeverityEnum) => {
    switch (severity) {
      case StealerLeakSeverityEnum.Critical:
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      case StealerLeakSeverityEnum.High:
        return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
      case StealerLeakSeverityEnum.Medium:
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
      case StealerLeakSeverityEnum.Low:
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case StealerLeakSeverityEnum.Info:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  // Get status color based on status
  const getStatusColor = (status?: Status111Enum) => {
    switch (status) {
      case Status111Enum.Open:
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case Status111Enum.InProgress:
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
      case Status111Enum.Resolved:
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case Status111Enum.FalsePositive:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      case Status111Enum.Duplicate:
        return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case Status111Enum.Retest:
        return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
      default:
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
    }
  };

  // Get validity color based on validity
  const getValidityColor = (validity?: ValidityEnum) => {
    switch (validity) {
      case ValidityEnum.Valid:
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case ValidityEnum.Invalid:
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      case ValidityEnum.New:
      default:
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
    }
  };

  // Get 2FA status color
  const get2FAStatusColor = (twoFaStatus?: TwoFaStatusEnum) => {
    switch (twoFaStatus) {
      case TwoFaStatusEnum.Enabled:
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case TwoFaStatusEnum.Disabled:
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      case TwoFaStatusEnum.None:
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-lg bg-purple-500/10">
              <Key className="h-6 w-6 text-purple-500" />
            </div>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-bold text-white">Stealer Leak Details</h1>
                <Badge
                  className={getStatusColor(leak?.status)}
                >
                  {leak?.status ? (leak.status === Status111Enum.InProgress ? 'In Progress' : 
                    leak.status === Status111Enum.FalsePositive ? 'False Positive' : 
                    leak.status.charAt(0).toUpperCase() + leak.status.slice(1)) : 'Open'}
                </Badge>
                <Badge
                  className={getSeverityColor(leak?.severity)}
                >
                  {leak?.severity ? leak.severity.toUpperCase() : 'High'}
                </Badge>
                <div className="flex items-center text-emerald-500">
                  <Check className="h-4 w-4 mr-1" />
                  {leak?.readed ? 'Read' : 'Unread'}
                </div>
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <span className="text-sm text-gray-400">Email: {leak?.identifier}</span>
                <span className="text-sm text-gray-400">Domain: {leak?.domainName}</span>
                <span className="text-sm text-gray-400">Service: {leak?.serviceName}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                // TODO: Implement export functionality
                console.log('Export clicked');
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button
              onClick={fetchLeak}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
      </div>
      <div className="space-y-4">
        {loading || error ? (
          <>
            {loading && (
              <div className="flex items-center justify-center h-12">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
              </div>
            )}
            {error && (
              <div className="text-red-500">{error}</div>
            )}
          </>
        ) : (
            <>
              {/* Main Content - 2 columns */}
              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h2 className="text-lg font-semibold text-white mb-4">Leak Information</h2>
                <div className="grid grid-cols-3 gap-6">
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-400">Password</div>
                      <div className="text-sm text-white mt-1">{leak?.password}</div>
                    </div>
      
                    <div>
                      <div className="text-sm text-gray-400">URL</div>
                      <div className="text-sm text-white mt-1">{leak?.url}</div>
                    </div>

                    <div>
                      <div className="text-sm text-gray-400">Device Info</div>
                      <div className="text-sm text-white mt-1">{leak?.deviceInfo || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-400">Created At</div>
                      <div className="text-sm text-white mt-1">{formatTimestamp(leak?.createdAt)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Updated At</div>
                      <div className="text-sm text-white mt-1">{formatTimestamp(leak?.updatedAt)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">2FA Status</div>
                      <div className="mt-1">
                        {currentTeam?.isAdmin ? (
                          <FilterSelect
                            value={leak?.twoFaStatus || TwoFaStatusEnum.None}
                            onValueChange={(value) => handleAttributeChange('twoFaStatus', value)}
                            options={[
                              { value: TwoFaStatusEnum.Enabled, label: 'Enabled', color: get2FAStatusColor(TwoFaStatusEnum.Enabled) },
                              { value: TwoFaStatusEnum.Disabled, label: 'Disabled', color: get2FAStatusColor(TwoFaStatusEnum.Disabled) },
                              { value: TwoFaStatusEnum.None, label: 'None', color: get2FAStatusColor(TwoFaStatusEnum.None) }
                            ]}
                            placeholder="Select 2FA Status"
                            width="w-[150px]"
                            triggerColor={get2FAStatusColor(leak?.twoFaStatus)}
                          />
                        ) : (
                          <div className={`w-[150px] px-4 py-2 text-sm bg-[#1A1F2E] border border-gray-800 rounded-lg text-gray-300 ${get2FAStatusColor(leak?.twoFaStatus)}`}>
                            {leak?.twoFaStatus ? leak.twoFaStatus.charAt(0).toUpperCase() + leak.twoFaStatus.slice(1) : 'None'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-400">Validity</div>
                      <div className="mt-1">
                        {currentTeam?.isAdmin ? (
                          <FilterSelect
                            value={leak?.validity || ValidityEnum.New}
                            onValueChange={(value) => handleAttributeChange('validity', value)}
                            options={[
                              { value: ValidityEnum.New, label: 'New', color: getValidityColor(ValidityEnum.New) },
                              { value: ValidityEnum.Valid, label: 'Valid', color: getValidityColor(ValidityEnum.Valid) },
                              { value: ValidityEnum.Invalid, label: 'Invalid', color: getValidityColor(ValidityEnum.Invalid) }
                            ]}
                            placeholder="Select Validity"
                            width="w-[150px]"
                            triggerColor={getValidityColor(leak?.validity)}
                          />
                        ) : (
                          <div className={`w-[150px] px-4 py-2 text-sm bg-[#1A1F2E] border border-gray-800 rounded-lg text-gray-300 ${getValidityColor(leak?.validity)}`}>
                            {leak?.validity ? leak.validity.charAt(0).toUpperCase() + leak.validity.slice(1) : 'New'}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Status</div>
                      <div className="mt-1">
                        {currentTeam?.isAdmin ? (
                          <FilterSelect
                            value={leak?.status || Status111Enum.Open}
                            onValueChange={(value) => handleAttributeChange('status', value)}
                            options={[
                              { value: Status111Enum.Open, label: 'Open', color: getStatusColor(Status111Enum.Open) },
                              { value: Status111Enum.InProgress, label: 'In Progress', color: getStatusColor(Status111Enum.InProgress) },
                              { value: Status111Enum.Resolved, label: 'Resolved', color: getStatusColor(Status111Enum.Resolved) },
                              { value: Status111Enum.FalsePositive, label: 'False Positive', color: getStatusColor(Status111Enum.FalsePositive) },
                              { value: Status111Enum.Duplicate, label: 'Duplicate', color: getStatusColor(Status111Enum.Duplicate) },
                              { value: Status111Enum.Retest, label: 'Retest', color: getStatusColor(Status111Enum.Retest) }
                            ]}
                            placeholder="Select Status"
                            width="w-[150px]"
                            triggerColor={getStatusColor(leak?.status)}
                          />
                        ) : (
                          <div className={`w-[150px] px-4 py-2 text-sm bg-[#1A1F2E] border border-gray-800 rounded-lg text-gray-300 ${getStatusColor(leak?.status)}`}>
                            {leak?.status ? (
                              leak.status === Status111Enum.InProgress ? 'In Progress' : 
                              leak.status === Status111Enum.FalsePositive ? 'False Positive' : 
                              leak.status.charAt(0).toUpperCase() + leak.status.slice(1)
                            ) : 'Open'}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Severity</div>
                      <div className="mt-1">
                        {currentTeam?.isAdmin ? (
                          <FilterSelect
                            value={leak?.severity || StealerLeakSeverityEnum.High}
                            onValueChange={(value) => handleAttributeChange('severity', value)}
                            options={[
                              { value: StealerLeakSeverityEnum.Critical, label: 'CRITICAL', color: getSeverityColor(StealerLeakSeverityEnum.Critical)},
                              { value: StealerLeakSeverityEnum.High, label: 'HIGH', color: getSeverityColor(StealerLeakSeverityEnum.High)},
                              { value: StealerLeakSeverityEnum.Medium, label: 'MEDIUM', color: getSeverityColor(StealerLeakSeverityEnum.Medium)},
                              { value: StealerLeakSeverityEnum.Low, label: 'LOW', color: getSeverityColor(StealerLeakSeverityEnum.Low)},
                              { value: StealerLeakSeverityEnum.Info, label: 'INFO', color: getSeverityColor(StealerLeakSeverityEnum.Info)}
                            ]}
                            placeholder="Select Severity"
                            width="w-[150px]"
                            triggerColor={getSeverityColor(leak?.severity)}
                          />
                        ) : (
                          <div className={`w-[150px] px-4 py-2 text-sm bg-[#1A1F2E] border border-gray-800 rounded-lg text-gray-300 ${getSeverityColor(leak?.severity)}`}>
                            {leak?.severity ? leak.severity.toUpperCase() : 'HIGH'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Impact and Recommendation */}
              <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
                <h2 className="text-lg font-semibold text-white mb-4">Analysis</h2>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-gray-400 mb-2">Impact</div>
                    {currentTeam?.isAdmin ? (
                      <div className="space-y-2">
                        <textarea
                          className="w-full px-3 py-2 bg-[#1A1F2E] border border-gray-800 rounded-md text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-h-[120px]"
                          value={impactText}
                          onChange={(e) => setImpactText(e.target.value)}
                          placeholder="Describe the potential impact of this leak..."
                        />
                        <div className="flex justify-end">
                          <Button 
                            size="sm"
                            className="bg-blue-500 text-white hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handleAttributeChange('impact', impactText)}
                            disabled={isUpdating || impactText === leak?.impact}
                          >
                            {isUpdating ? 'Saving...' : 'Save Impact'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full px-3 py-2 bg-[#1A1F2E] border border-gray-800 rounded-md text-white text-sm min-h-[120px] overflow-auto">
                        {leak?.impact || 'No impact information available'}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-2">Recommendation</div>
                    {currentTeam?.isAdmin ? (
                      <div className="space-y-2">
                        <textarea
                          className="w-full px-3 py-2 bg-[#1A1F2E] border border-gray-800 rounded-md text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-h-[120px]"
                          value={recommendationText}
                          onChange={(e) => setRecommendationText(e.target.value)}
                          placeholder="Provide recommendations for handling this leak..."
                        />
                        <div className="flex justify-end">
                          <Button 
                            size="sm"
                            className="bg-blue-500 text-white hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handleAttributeChange('recommendation', recommendationText)}
                            disabled={isUpdating || recommendationText === leak?.recommendation}
                          >
                            {isUpdating ? 'Saving...' : 'Save Recommendation'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="w-full px-3 py-2 bg-[#1A1F2E] border border-gray-800 rounded-md text-white text-sm min-h-[120px] overflow-auto">
                        {leak?.recommendation || 'No recommendations available'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <AssetNotes objectId={id || ""} objectType="stealerleak" />
              
            </>
        )}
      </div>
      
      
      {/* {leak && (
        <div className="mt-4">
          <h2 className="text-lg font-bold text-white">Leak Details</h2>
          <div className="mt-2">
            <p className="text-gray-400">ID: {leak.id}</p>
            <p className="text-gray-400">Title: {leak.identifier}</p>
            <p className="text-gray-400">Password: {leak.password}</p>
            <p className="text-gray-400">URL: {leak.url}</p>
            <p className="text-gray-400">Device: {leak.device}</p>
            <p className="text-gray-400">Device Info: {leak.deviceInfo}</p>
            <p className="text-gray-400">Created At: {formatTimestamp(leak.createdAt)}</p>
          </div>
        </div>
      )} */}
    </div>
  );
}

export default StealerLeakDetail